/** Hermes 旅程手册创作会话：context / 复用键（见 2026-08-07 journey-authoring design）。 */

export type JourneyAuthoringContext = {
  mode: "journey_authoring";
  accountId: string;
  brandId: number;
  /** 已有旅程数字 id；新建为 null（禁止 "new" 字符串） */
  journeyId: number | null;
};

export function journeyAuthoringChatContext(ctx: JourneyAuthoringContext) {
  return {
    mode: "journey_authoring" as const,
    brand_id: ctx.brandId,
    journey_id: ctx.journeyId,
  };
}

/** 退出创作能力时清 mode（保留 brand_id/journey_id 便于再开）；不删消息。 */
export function clearJourneyAuthoringModeContext() {
  return { mode: null as null };
}

function coerceJourneyId(value: unknown): number | null {
  if (value == null || value === "" || value === "null" || value === "new") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function sameJourneyId(a: number | null, b: number | null): boolean {
  return a === b;
}

/** 会话是否为旅程创作，且 brand + journey（含 null 新建）匹配。 */
export function matchesJourneyAuthoringChat(
  chat: { context?: Record<string, unknown> | null },
  ctx: Pick<JourneyAuthoringContext, "brandId" | "journeyId">,
): boolean {
  const context = chat.context;
  if (!context || typeof context !== "object") return false;
  if (context.mode !== "journey_authoring") return false;
  if (Number(context.brand_id) !== ctx.brandId) return false;
  return sameJourneyId(coerceJourneyId(context.journey_id), ctx.journeyId);
}

/** 默认恢复会话时跳过创作会话，避免 FAB/客户窗误接。 */
export function isJourneyAuthoringChat(chat: {
  context?: Record<string, unknown> | null;
}): boolean {
  return chat.context?.mode === "journey_authoring";
}

/** 拒绝把字符串 "new" 当成有效 journey id。 */
export function normalizeJourneyAuthoringId(raw: unknown): number | null {
  return coerceJourneyId(raw);
}

/** 从已加载会话还原创作上下文（切换会话 / 刷新 chip）。 */
export function journeyAuthoringFromChat(
  chat: { context?: Record<string, unknown> | null },
  accountId: string,
): JourneyAuthoringContext | null {
  const context = chat.context;
  if (!context || typeof context !== "object") return null;
  if (context.mode !== "journey_authoring") return null;
  const brandId = Number(context.brand_id);
  if (!Number.isFinite(brandId) || brandId <= 0) return null;
  return {
    mode: "journey_authoring",
    accountId,
    brandId,
    journeyId: coerceJourneyId(context.journey_id),
  };
}

/**
 * 从当前路径推断旅程创作上下文（编辑页 / 新建页）。
 * 非旅程编辑路由返回 null（FAB 不强开旅程）。
 */
export function parseJourneyAuthoringRoute(
  pathname: string,
  accountId: string,
): JourneyAuthoringContext | null {
  if (!accountId || !pathname) return null;
  const match = pathname.match(
    /\/accounts\/([^/]+)\/brands\/(\d+)\/apps\/journey\/journeys\/([^/?#]+)/,
  );
  if (!match) return null;
  if (match[1] !== accountId) return null;
  const brandId = Number(match[2]);
  if (!Number.isFinite(brandId) || brandId <= 0) return null;
  const rawJourney = match[3];
  return {
    mode: "journey_authoring",
    accountId,
    brandId,
    journeyId: rawJourney === "new" ? null : coerceJourneyId(rawJourney),
  };
}

/** 能力条短标签：旅程 + 可选后缀。 */
export function journeyCapabilityChipLabel(
  ctx: Pick<JourneyAuthoringContext, "journeyId">,
): string {
  if (ctx.journeyId == null) return "旅程 · 新建";
  return `旅程 · #${ctx.journeyId}`;
}
